import { Inject } from '@odinjs/odin';
import { ContextType, Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { SubmittableFragmentContext } from '@philips-internal/oswald-ui-core/lib/component/context';
import { SubmitEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpResponse } from '@philips-internal/oswald-ui-core/lib/services/http-service';
import { getFoundationDataContext } from '../../app/bootstrap-foundation';
import { HttpStatus } from '../../constants/HttpStatus';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import { APPLICATION_ROUTES } from '../../constants/application-routes';
import { HttpServiceError, PatientHttpService } from '../../services/patient-http-service';
import { showToastNotificationError, showToastNotificationWarning } from '../../utils/toast-notification-utils';
import BaseController from '../BaseController';
import { BasicFile } from '@philips-internal/oswald-ui-components/lib/components/file-uploader/base-file-uploader-types';
import { AccountCreationResult, FileUploadRequest, FileUploadResponse, UploadedFiles, ValidationErrorProps } from '../../interfaces/account-creation';
import { isEmptyValue } from '@philips-internal/oswald-ui-utils';

@Controller({ component: 'layout-account-creation-processing', fragment: 'account-creation-processing' })
export default class AccountCreationModalController extends BaseController {

  @Inject(ContextType.SUBMITTABLE_FRAGMENT)
    submittableFragmentContext!: SubmittableFragmentContext;

  @OnMount({ target: 'layout-account-creation-processing' })
  onMountLayoutAccountCreationCompleted(): void {
    const body = this.context.get('body') as Record<string, Value>;
    const memoryFiles = this.context.get('memoryFiles') as Map<string, Blob>;
    const infoUploadedFiles = this.context.get('infoUploadedFiles') as BasicFile[];
    if(!body) {
      return;
    }

    const { validationType, personPhone } = body;
    const applicationContext = getFoundationDataContext();
    applicationContext.set({validationType});

    if (isEmptyValue(memoryFiles?.size) || isEmptyValue(infoUploadedFiles)) {
      const bodyWithoutFiles = { ...body, files: []};
      PatientHttpService.post(APPLICATION_ENDPOINTS.CREATE_NEW_ACCOUNT, {}, { body: bodyWithoutFiles as Record<string, Value> })
        .then(r => this.handleCreationSuccess(r, validationType as string, personPhone as string))
        .catch((e: HttpServiceError) => this.handleCreationError(e));
      return;
    }

    const uploadDocumentsRequestPromises: Array<Promise<FileUploadRequest>> = [];
    const uploadedFiles = this.joinInfoUploadedFilesWithMemoryFiles(infoUploadedFiles, memoryFiles);

    uploadedFiles.forEach(uploaded => {
      const formData = new FormData();
      formData.append('file', uploaded.file);

      const uploadedFilePromise = PatientHttpService.post(APPLICATION_ENDPOINTS.UPLOAD_DOCUMENTS, {}, { body: formData })
        .then(response => {
          const fileUpload = response.body as FileUploadResponse;
          const fileUploadRequest: FileUploadRequest = {
            fileName: fileUpload.fileName,
            path: fileUpload.path,
            sequenceNumberFileRule: Number(uploaded.info.predefinedFileKey),
          }
          return fileUploadRequest;
        });

      uploadDocumentsRequestPromises.push(uploadedFilePromise);
    });

    Promise.all(uploadDocumentsRequestPromises).then(response => {{
      console.log(response);

      const bodyWithFiles = { ...body, files: response};
      PatientHttpService.post(APPLICATION_ENDPOINTS.CREATE_NEW_ACCOUNT, {}, { body: bodyWithFiles as Record<string, Value> })
        .then(r => this.handleCreationSuccess(r, validationType as string, personPhone as string))
        .catch((e: HttpServiceError) => this.handleCreationError(e));
    }});
  }

  private async handleCreationSuccess(response: HttpResponse<unknown>, validationType: string, phoneNumber: string ): Promise<HttpResponse<unknown>> {
    const { status } = response;
    const result = response.body as AccountCreationResult;
    const applicationContext = getFoundationDataContext();
    applicationContext.set({'dsLogin': result.dsLogin});
    applicationContext.set({'phoneNumber': phoneNumber});

    this.submittableFragmentContext.submit(new SubmitEvent( { record:{ validationType } } ));
    this.historyService.push(APPLICATION_ROUTES.ACCOUNT_CREATION_COMPLETED );

    if (status !== 201) {
      throw 'Internal Server Error';
    }

    return response;
  }

  private handleCreationError({ response }: HttpServiceError): void {
    if(response?.status === HttpStatus.UNPROCESSABLE_ENTITY && response?.body) {
      const resp = response.body as ValidationErrorProps;
      this.submittableFragmentContext.cancel(new SubmitEvent());
      showToastNotificationWarning(resp.message);
      return;
    }

    let errorMessage = 'newaccount.error.internal.server.error.try.again';

    if (response?.body) {
      const result = response.body as AccountCreationResult;
      errorMessage = result.messagekey;
    }

    showToastNotificationError(errorMessage);
    this.submittableFragmentContext.cancel(new SubmitEvent());
  }

  private joinInfoUploadedFilesWithMemoryFiles(infoUploadedFiles: BasicFile[], memoryFiles: Map<string, Blob>): UploadedFiles[] {
    const uploadedFiles: UploadedFiles[] = [];
    infoUploadedFiles.forEach(element => {
      if (element && memoryFiles.has(element.filepath)){
        uploadedFiles.push({
          file: memoryFiles.get(element.filepath),
          info: element
        } as UploadedFiles);
      }
    });

    return uploadedFiles;
  }
}
